import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Sparkles, Eye, EyeOff, Upload, X } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { PageTitle, Card, Field, inputCls, GoldButton } from "@/admin/ui";

const FIELDS = [
  ["badge", "Selo (badge)", "text"],
  ["title", "Título", "text"],
  ["subtitle", "Subtítulo", "text"],
  ["description", "Descrição", "textarea"],
  ["primary_cta_label", "Botão principal — Texto", "text"],
  ["primary_cta_url", "Botão principal — Link", "text"],
  ["secondary_cta_label", "Botão secundário — Texto", "text"],
  ["secondary_cta_url", "Botão secundário — Link", "text"],
];

export const FeaturedLaunchManager = () => {
  const [settings, setSettings] = useState(null);
  const [fl, setFl] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      setSettings(data);
      setFl({ enabled: true, ...(data.featured_launch || {}) });
    });
  }, []);

  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Imagem muito grande (máx. 8MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFl((p) => ({ ...p, image: data.url }));
      toast.success("Imagem enviada. Não esqueça de salvar.");
    } catch {
      toast.error("Falha no upload da imagem.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const { key, _id, ...payload } = settings;
      payload.featured_launch = fl;
      await api.put("/admin/settings", payload);
      toast.success("Lançamento salvo — atualização no site em segundos.");
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const update = (k, v) => setFl((p) => ({ ...p, [k]: v }));

  if (!settings) return <div className="text-white/40">Carregando...</div>;

  return (
    <div data-testid="admin-featured-launch">
      <PageTitle
        title="Lançamento do Momento"
        subtitle="Aparece na tela de login/registro. Todos os campos são refletidos ao vivo no site."
        action={
          <div className="flex items-center gap-3">
            <button
              onClick={() => update("enabled", !fl.enabled)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                fl.enabled
                  ? "border-[#F5C542]/50 text-[#F5C542] bg-[#F5C542]/10"
                  : "border-white/10 text-white/50"
              }`}
              data-testid="fl-toggle"
            >
              {fl.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {fl.enabled ? "Visível" : "Oculto"}
            </button>
            <GoldButton onClick={save} disabled={saving} data-testid="fl-save">
              <Save className="w-4 h-4 inline mr-1" /> {saving ? "Salvando..." : "Salvar"}
            </GoldButton>
          </div>
        }
      />

      <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
        <Card>
          <h3 className="font-display font-bold text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#F5C542]" /> Conteúdo do lançamento
          </h3>

          {/* Image upload block */}
          <div className="mb-6 p-4 rounded-2xl" style={{ background: "rgba(245, 197, 66,0.06)", border: "1px solid rgba(245, 197, 66,0.25)" }}>
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/50 font-medium mb-3">
              Imagem do lançamento
            </div>
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="relative w-32 h-40 shrink-0 rounded-xl overflow-hidden border border-[#F5C542]/30 bg-black/40">
                {fl.image ? (
                  <img src={imgUrl(fl.image)} alt="preview" className="w-full h-full object-cover" data-testid="fl-image-thumb" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-white/30 text-center px-2">
                    Sem imagem
                  </div>
                )}
                {fl.image && (
                  <button
                    onClick={() => setFl((p) => ({ ...p, image: "" }))}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 border border-white/20 flex items-center justify-center text-white/70 hover:text-red-300 hover:border-red-400/50"
                    title="Remover imagem"
                    data-testid="fl-image-remove"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="flex-1 space-y-3 min-w-0">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-[#F5C542] to-[#F28C28] text-white hover:scale-[1.02] transition-transform disabled:opacity-50"
                    data-testid="fl-image-upload"
                  >
                    <Upload className="w-4 h-4" /> {uploading ? "Enviando..." : "Enviar imagem"}
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadImage(e.target.files?.[0])}
                    data-testid="fl-image-file-input"
                  />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">
                    ou cole uma URL
                  </div>
                  <input
                    value={fl.image ?? ""}
                    onChange={(e) => setFl((p) => ({ ...p, image: e.target.value }))}
                    placeholder="https://... ou /caminho-relativo.jpg"
                    className={inputCls}
                    data-testid="fl-image"
                  />
                </div>
                <div className="text-[11px] text-white/40 leading-relaxed">
                  Formato ideal: proporção 4:5 (retrato). Máximo 8MB. A imagem é redimensionada e otimizada automaticamente.
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {FIELDS.map(([k, label, type]) => (
              <Field key={k} label={label} hint={k === "image" ? "URL absoluta ou /caminho relativo" : undefined}>
                {type === "textarea" ? (
                  <textarea
                    rows={4}
                    value={fl[k] ?? ""}
                    onChange={(e) => update(k, e.target.value)}
                    className={`${inputCls} resize-y`}
                    data-testid={`fl-${k}`}
                  />
                ) : (
                  <input
                    value={fl[k] ?? ""}
                    onChange={(e) => update(k, e.target.value)}
                    className={inputCls}
                    data-testid={`fl-${k}`}
                  />
                )}
              </Field>
            ))}
          </div>
        </Card>

        <Card>
          <h3 className="font-display font-bold text-white mb-4">Pré-visualização</h3>
          <div className="rounded-2xl overflow-hidden border border-[#F5C542]/30 bg-black/40">
            {fl.image ? (
              <img src={imgUrl(fl.image)} alt={fl.title} className="w-full aspect-[4/5] object-cover" />
            ) : (
              <div className="aspect-[4/5] flex items-center justify-center text-white/30 text-xs">
                Sem imagem
              </div>
            )}
          </div>
          <div className="mt-4 space-y-2 text-center">
            <div className="text-[10px] tracking-[0.24em] uppercase text-[#F5C542] font-bold">{fl.badge || "—"}</div>
            <div className="font-display font-bold text-white text-lg leading-tight">{fl.title || "—"}</div>
            <div className="text-xs italic text-white/55">{fl.subtitle || ""}</div>
            {fl.description && <p className="text-xs text-white/60 mt-2 leading-relaxed">{fl.description}</p>}
            {fl.primary_cta_label && (
              <div className="btn-gold rounded-full px-4 py-2 font-display font-bold text-xs uppercase inline-block mt-3">
                {fl.primary_cta_label}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
